// Formatos de torneo: presets que rellenan el alta y el vocabulario de la cadena de desempate.
//
// Los presets viven acá, como constante, y no en una tabla: con un solo reglamento real (Catan)
// generalizar a un catálogo editable es cómo se construye la abstracción equivocada. El staff
// elige uno y ajusta; todo queda editable.
//
// ⚠️ La cadena y el plan de etapas son ESPEJO de backend/src/utils/tournamentStandings.js y
// tournamentBracket.js. El backend es la autoridad (valida al guardar); acá sólo se arman los
// valores y se explican.

export const CRITERIOS = {
    wins: { label: 'Victorias', ayuda: 'cuántas mesas ganó' },
    raw: { label: 'PV', ayuda: 'total de puntos de victoria' },
    raw_share: { label: '% PV', ayuda: 'sus PV sobre los PV anotados en las mesas donde jugó' },
    seconds: { label: '2º puestos', ayuda: 'cuántas veces salió 2º (un empate cuenta para los dos)' },
    thirds: { label: '3º puestos', ayuda: 'cuántas veces salió 3º' },
    points: { label: 'Puntos', ayuda: 'los puntos del torneo, según la tabla o la fórmula' },
};

export const CADENA_CATAN = ['wins', 'raw', 'raw_share', 'seconds', 'thirds'];

// Bases Devir 2025: el formato depende de cuánta gente juega. El tramo se congela al generar la
// ronda 1, con los que están sentados.
export const PLAN_CATAN = {
    tiers: [
        { min: 1, max: 28, label: '12 a 28 anotados', stages: [{ type: 'final', size: 4 }] },
        {
            min: 29, max: 79, label: '29 a 79 anotados',
            stages: [
                { type: 'semifinal', size: 16, tableSize: 4, seeding: 'serpentine', advancePerTable: 1 },
                { type: 'final', size: 4 },
            ],
        },
        {
            min: 80, max: null, label: '80 o más anotados',
            stages: [
                { type: 'cut', criterion: 'wins', min: 1 },
                { type: 'qualifier', rounds: 1 },
                { type: 'semifinal', size: 16, tableSize: 4, seeding: 'serpentine', advancePerTable: 1 },
                { type: 'final', size: 4 },
            ],
        },
    ],
};

export const PRESETS = [
    {
        key: 'catan_oficial',
        label: 'Catan · clasificatorio oficial (bases Devir 2025)',
        resumen: '3 rondas por victorias, final de 4 (semifinal de 16 si se anotan 29 o más). '
            + 'Desempate: victorias → PV → % PV → 2º → 3º, y después el juez.',
        values: {
            gameName: 'Catan',
            sessionsPlanned: null,
            tableSize: 4,
            minTableSize: 3,
            roundsPlanned: 3,
            pairingMode: 'swiss',
            avoidRematch: true,
            scoringMode: 'table',
            scoreMin: 0,
            scoreMax: 1,
            scoreTable: { 4: [1, 0, 0, 0], 3: [1, 0, 0] },
            usesRawScore: true,
            rankBy: CADENA_CATAN,
            finalTableSize: 4,
            bracketPlan: PLAN_CATAN,
        },
    },
    {
        key: 'enigma_clasico',
        label: 'Torneo Enigma clásico',
        resumen: 'Puntos por puesto (7 al que gana, 1 al último), suizo, final de 4.',
        values: {
            sessionsPlanned: null,
            tableSize: 4,
            minTableSize: 3,
            roundsPlanned: 3,
            pairingMode: 'swiss',
            avoidRematch: true,
            scoringMode: 'formula',
            scoreMin: 1,
            scoreMax: 7,
            scoreTable: null,
            usesRawScore: true,
            rankBy: null,
            tiebreaker: 'wins_then_raw',
            finalTableSize: 4,
            bracketPlan: null,
        },
    },
    {
        key: 'liga',
        label: 'Liga de varias jornadas',
        resumen: '8 jornadas de 2 rondas; la tabla general suma todo y la gana el primero, sin final.',
        values: {
            sessionsPlanned: 8,
            tableSize: 4,
            minTableSize: 3,
            roundsPlanned: 2,
            pairingMode: 'swiss',
            avoidRematch: true,
            scoringMode: 'formula',
            scoreMin: 1,
            scoreMax: 7,
            scoreTable: null,
            usesRawScore: true,
            rankBy: null,
            tiebreaker: 'wins_then_raw',
            finalTableSize: null,
            bracketPlan: null,
        },
    },
];

export const presetPorKey = (key) => PRESETS.find((p) => p.key === key) || null;

export function parseJson(texto) {
    if (texto == null || texto === '') return null;
    if (typeof texto === 'object') return texto;
    try { return JSON.parse(texto); } catch (e) { return null; }
}

// "3 rondas → semifinal de 16 → final de 4", para las etapas que rigen.
export function describirEtapas(rondas, stages) {
    const parts = [`${rondas} ronda${Number(rondas) === 1 ? '' : 's'}`];
    (stages || []).forEach((s) => {
        if (s.type === 'cut') parts.push(`corte a los de ${s.min - 1} victoria${s.min - 1 === 1 ? '' : 's'}`);
        if (s.type === 'qualifier') parts.push(`${s.rounds} preliminar${s.rounds === 1 ? '' : 'es'} más`);
        if (s.type === 'semifinal') parts.push(`semifinal de ${s.size}`);
        if (s.type === 'final') parts.push(`final de ${s.size}`);
    });
    return parts.join(' → ');
}

// Las etapas que rigen para un torneo (fila del modelo), con `players` para la vista previa de un
// plan con tramos todavía sin congelar.
export function etapasDe(t, players) {
    const plan = parseJson(t && t.bracket_plan);
    if (!plan) return t && t.final_table_size ? [{ type: 'final', size: Number(t.final_table_size) }] : [];
    if (Array.isArray(plan.stages)) return plan.stages;
    const n = Number(players) || 0;
    let idx = plan.resolved && Number.isInteger(plan.resolved.tier) ? plan.resolved.tier
        : plan.tiers.findIndex((x) => n >= x.min && (x.max == null || n <= x.max));
    if (idx < 0) idx = 0;
    return (plan.tiers[idx] || plan.tiers[0]).stages;
}

export const tieneEtapa = (t, tipo, players) => etapasDe(t, players).some((s) => s.type === tipo);

// Cuántas preliminares se juegan en total (las base + las que agrega el plan).
export const totalPreliminares = (t, players) => Number(t.rounds_planned)
    + etapasDe(t, players).filter((s) => s.type === 'qualifier').reduce((a, s) => a + s.rounds, 0);

// Espejo de `splitOptions` del backend (utils/tournamentPairing.js): TODAS las formas de repartir
// n jugadores en mesas de entre `min` y `max`, sentando a todos. Si el rango sale vacío se estira
// el máximo en uno (14 con mesas de 3 → 4·4·3·3) antes que armar una mesa por debajo del mínimo.
export function opcionesDeReparto(n, min, max) {
    const mx = Number(max) || 4;
    const mn = Math.max(2, Number(min) || 2);
    if (!Number.isInteger(n) || n < 2) return [];
    const reparto = (mesas) => {
        const base = Math.floor(n / mesas);
        const resto = n % mesas;
        const sizes = [];
        for (let i = 0; i < resto; i++) sizes.push(base + 1);
        for (let i = 0; i < mesas - resto; i++) sizes.push(base);
        return sizes;
    };
    const tMin = Math.ceil(n / mx);
    const tMax = Math.floor(n / mn);
    const conAviso = (o) => ({ ...o, canAvoid: o.sizes.length >= Math.max(...o.sizes) });
    if (tMin > tMax) {
        if (tMax >= 2) {
            const up = reparto(tMax);
            if (Math.max(...up) <= mx + 1) return [conAviso({ tables: tMax, sizes: up, relajado: 'max' })];
        }
        return [conAviso({ tables: tMin, sizes: reparto(tMin), relajado: 'min' })];
    }
    const out = [];
    for (let k = tMin; k <= tMax; k++) out.push(conAviso({ tables: k, sizes: reparto(k), relajado: false }));
    return out;
}

export const ACCIONES_BITACORA = {
    created: 'Torneo creado',
    config_changed: 'Cambió la configuración',
    tier_selected: 'Se fijó el formato por cantidad de anotados',
    roll_call: 'Pasó lista',
    late_entry: 'Llegada tarde',
    participant_dropped: 'Baja de un participante',
    participant_reactivated: 'Reactivó a un participante',
    consent_marked: 'Marcó "informado"',
    consent_cleared: 'Desmarcó "informado"',
    seat_no_show: 'No se presentó a la mesa',
    seat_played: 'Volvió a la mesa',
    round_rebuilt: 'Rearmó la ronda',
    round_manual: 'Armó la ronda a mano',
    seating_changed: 'Movió jugadores entre mesas',
    round_reopened: 'Reabrió una ronda',
    cut_applied: 'Corte por victorias',
    tiebreak_set: 'El juez ordenó un empate',
    tiebreak_cleared: 'Borró un desempate del juez',
    tiebreaks_reset: 'Se anularon los desempates del juez',
    semifinal_built: 'Armó la semifinal',
    semifinal_discarded: 'Desarmó la semifinal',
    final_built: 'Armó la mesa final',
    final_discarded: 'Desarmó la mesa final',
    result_corrected: 'Corrigió un resultado',
    export_downloaded: 'Bajó la planilla para Devir',
    event_linked: 'Enganchó el torneo a un evento',
    event_unlinked: 'Desenganchó el torneo del evento',
};
